import {Component, Inject, OnDestroy, OnInit, ElementRef, Renderer2} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {Router, NavigationEnd} from '@angular/router';

import {Subject} from 'rxjs';
import {takeUntil, distinctUntilChanged} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import * as Waves from 'node-waves';

import {CoreMenuService} from '@core/components/core-menu/core-menu.service';
import {CoreSidebarService} from '@core/components/core-sidebar/core-sidebar.service';
import {CoreConfigService} from '@core/services/config.service';
import {CoreLoadingScreenService} from '@core/services/loading-screen.service';
import {CoreTranslationService} from '@core/services/translation.service';

import {menuParceiro} from 'app/shared/menu/menuParceiro';
import {menuAdministradorPlataforma} from "./shared/menu/menuAdministradorPlataforma";
import {data} from "autoprefixer";
import {CoreMenu} from "../@core/types";
import {AutenticacaoService} from "./shared/clients/user-client";
import {PushNotificationService} from './services/push-notification.service';
import {NotificationsService} from './layout/components/navbar/navbar-notification/notifications.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    menu: any;
    coreConfig: any;
    private currentPusherUserId: string | null = null;
    public isPublicRoute: boolean = false;

    // Private
    private _unsubscribeAll: Subject<any>;

    constructor(
        @Inject(DOCUMENT) private document: any,
        private _title: Title,
        private _renderer: Renderer2,
        private _elementRef: ElementRef,
        private _coreMenuService: CoreMenuService,
        private _translateService: TranslateService,
        public _coreConfigService: CoreConfigService,
        private _coreSidebarService: CoreSidebarService,
        private _coreLoadingScreenService: CoreLoadingScreenService,
        private autenticacaoService: AutenticacaoService,
        private pushNotificationService: PushNotificationService,
        private notificationsService: NotificationsService,
        private router: Router
    ) {
        this._unsubscribeAll = new Subject();
        
        // Verificar rota pública IMEDIATAMENTE no construtor
        // Usar window.location como fallback se router.url estiver vazio ou '/'
        const currentUrl = (this.router.url && this.router.url !== '/') 
            ? this.router.url 
            : window.location.pathname;
        this.isPublicRoute = currentUrl.startsWith('/campanha/');
        console.log('🔍 [Constructor] router.url:', this.router.url);
        console.log('🔍 [Constructor] window.location.pathname:', window.location.pathname);
        console.log('🔍 [Constructor] currentUrl:', currentUrl);
        console.log('🔍 [Constructor] isPublicRoute:', this.isPublicRoute);
        
        // Detectar mudanças de rota
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.isPublicRoute = event.url.startsWith('/campanha/');
                console.log('🔍 [NavigationEnd] URL:', event.url);
                console.log('🔍 [NavigationEnd] isPublicRoute:', this.isPublicRoute);

                // Garante desconexão do Pusher ao chegar na tela de login (logout manual ou redirecionamento)
                if (event.url.startsWith('/login')) {
                    console.log('AppComponent: Navegação para /login detectada - limpando notificações Pusher');
                    this.cleanupPusherNotifications();
                }
            }
        });
        
        // Initialize coreConfig with default values to prevent undefined errors
        this.coreConfig = {
            app: { appTitle: 'BuyHelp Backoffice' },
            layout: {
                type: 'vertical',
                navbar: { type: 'navbar-static-top', hidden: false },
                footer: { type: 'footer-static', hidden: false },
                menu: { hidden: false },
                skin: 'default'
            }
        };
    }

    ngOnInit(): void {
        Waves.init();
        
        // Set initial title with safe navigation
        if (this.coreConfig?.app?.appTitle) {
            this._title.setTitle(this.coreConfig.app.appTitle);
        }
        
        this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
            this.coreConfig = config || this.coreConfig; // Fallback to default if config is null
            
            // Protect DOM manipulation with null checks
            const rootElement = this._elementRef?.nativeElement;
            if (!rootElement) return;
            
            rootElement.classList.remove(
                'vertical-layout',
                'vertical-menu-modern',
                'horizontal-layout',
                'horizontal-menu'
            );
            // Add class based on config options
            if (this.coreConfig.layout.type === 'vertical') {
                rootElement.classList.add('vertical-layout', 'vertical-menu-modern');
            } else if (this.coreConfig.layout.type === 'horizontal') {
                rootElement.classList.add('horizontal-layout', 'horizontal-menu');
            }

            // Remove default classes first
            rootElement.classList.remove(
                'navbar-floating',
                'navbar-static',
                'navbar-sticky',
                'navbar-hidden'
            );

            // Add class based on config options
            if (this.coreConfig.layout.navbar.type === 'navbar-static-top') {
                rootElement.classList.add('navbar-static');
            } else if (this.coreConfig.layout.navbar.type === 'fixed-top') {
                rootElement.classList.add('navbar-sticky');
            } else if (this.coreConfig.layout.navbar.type === 'floating-nav') {
                rootElement.classList.add('navbar-floating');
            } else {
                rootElement.classList.add('navbar-hidden');
            }

            rootElement.classList.remove('footer-fixed', 'footer-static', 'footer-hidden');

            // Add class based on config options
            if (this.coreConfig.layout.footer.type === 'footer-sticky') {
                rootElement.classList.add('footer-fixed');
            } else if (this.coreConfig.layout.footer.type === 'footer-static') {
                rootElement.classList.add('footer-static');
            } else {
                rootElement.classList.add('footer-hidden');
            }

            if (
                this.coreConfig.layout.menu.hidden &&
                this.coreConfig.layout.navbar.hidden &&
                this.coreConfig.layout.footer.hidden
            ) {
                rootElement.classList.add('blank-page');
                // ! Fix: Transition issue while coming from blank page
                const appContent = rootElement.getElementsByClassName('app-content')[0];
                if (appContent) {
                    this._renderer.setAttribute(appContent, 'style', 'transition:none');
                }
            } else {
                rootElement.classList.remove('blank-page');
                // ! Fix: Transition issue while coming from blank page
                setTimeout(() => {
                    const appContent = rootElement.getElementsByClassName('app-content')[0];
                    if (appContent) {
                        this._renderer.setAttribute(appContent, 'style', 'transition:300ms ease all');
                    }
                }, 0);
                // If navbar hidden
                if (this.coreConfig.layout.navbar.hidden) {
                    rootElement.classList.add('navbar-hidden');
                }
                // Menu (Vertical menu hidden)
                if (this.coreConfig.layout.menu.hidden) {
                    this._renderer.setAttribute(rootElement, 'data-col', '1-column');
                } else {
                    this._renderer.removeAttribute(rootElement, 'data-col');
                }
                // Footer
                if (this.coreConfig.layout.footer.hidden) {
                    rootElement.classList.add('footer-hidden');
                }
            }

            // Skin Class (Adding to body as it requires highest priority)
            if (this.coreConfig.layout.skin !== '' && this.coreConfig.layout.skin !== undefined) {
                this.document.body.classList.remove('default-layout', 'bordered-layout', 'dark-layout', 'semi-dark-layout');
                this.document.body.classList.add(this.coreConfig.layout.skin + '-layout');
            }
            
            // Update title after config is loaded
            if (this.coreConfig?.app?.appTitle) {
                this._title.setTitle(this.coreConfig.app.appTitle);
            }
        });

        // Enhanced currentUser subscription with flexible filtering and Pusher integration
        // NÃO executar em rotas públicas
        console.log('🔍 [ngOnInit] isPublicRoute:', this.isPublicRoute);
        console.log('🔍 [ngOnInit] Vai carregar serviços autenticados?', !this.isPublicRoute);
        
        if (!this.isPublicRoute) {
            console.log('✅ [ngOnInit] Carregando serviços autenticados...');
            this.autenticacaoService.currentUser
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    distinctUntilChanged((prev: any, curr: any) => {
                        return this.getUserIdentifier(prev) === this.getUserIdentifier(curr);
                    })
                )
                .subscribe((user: any) => {
                    // Register menu if user has ACL functionalities
                    if (user?.acl?.funcionalidades) {
                        let menu: CoreMenu[] = this.removerItensOcultos(user.acl.funcionalidades);
                        
                        // Conditionally unregister menu only if it exists
                        if (this._coreMenuService.getMenu('main')) {
                            this._coreMenuService.unregister('main');
                        }
                        
                        this._coreMenuService.register('main', menu);
                        this._coreMenuService.setCurrentMenu('main');
                    }
                    
                    // Initialize Pusher notifications if we can identify the user; only cleanup on real logout
                    const userId = this.getUserIdentifier(user);
                    if (userId) {
                        this.initializePusherNotifications(userId);
                    } else if (user === null || user === undefined) {
                        this.cleanupPusherNotifications();
                    }
                });
            
            // Subscribe to Pusher notifications once (avoid multiple subscriptions)
            this.pushNotificationService.notificationReceived$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(notification => {
                    if (notification) {
                        this.handlePusherNotification(notification);
                    }
                });
        }
    }
    
    // Itens retirados do menu; a funcionalidade continua na ACL (rota e guard intactos)
    private readonly itensOcultosMenu = ['conciliador_credenciado', 'campanhas_credenciado'];

    private removerItensOcultos(itens: CoreMenu[]): CoreMenu[] {
        return (itens ?? [])
            .filter(item => !this.itensOcultosMenu.includes(item.id))
            .map(item => item.children ? {...item, children: this.removerItensOcultos(item.children)} : item);
    }

    private getUserIdentifier(user: any): string | null {
        if (user?._id) {
            return String(user._id);
        }
        return null;
    }

    private initializePusherNotifications(userId: string): void {
        // Avoid re-initializing for the same user
        if (this.currentPusherUserId === userId) {
            return;
        }
        
        this.currentPusherUserId = userId;
        
        // Save user ID for persistence after refresh
        localStorage.setItem('pusher_user_id', userId);
        
        // Request notification permission
        this.pushNotificationService.requestPermission();
        
        // Subscribe to notifications for this user
        this.pushNotificationService.subscribeToNotifications(userId);
        
        // Load notifications when initializing Pusher (covers login and refresh scenarios)
        this.loadNotificationsOnInit();
    }
    
    private cleanupPusherNotifications(): void {
        if (!this.currentPusherUserId) {
            return;
        }
        
        console.log('AppComponent: Limpando notificações Pusher no logout');
        this.pushNotificationService.unsubscribeFromNotifications();
        localStorage.removeItem('pusher_user_id');
        this.currentPusherUserId = null;
    }

    private handlePusherNotification(notification: any): void {
        // Show SweetAlert2 toast notification
        Swal.fire({
            toast: true,
            position: 'top-end',
            icon: 'info',
            title: notification.titulo || 'Nova Notificação',
            text: notification.mensagem || '',
            showConfirmButton: false,
            timer: 4000,
            timerProgressBar: true
        });
        
        // Update notification counter by fetching latest notifications
        this.notificationsService.getNotificationsData();
    }

    /**
     * Carrega notificações na inicialização do Pusher
     */
    private loadNotificationsOnInit(): void {
        try {
            console.log('AppComponent: Carregando notificações na inicialização...');
            
            // Busca as notificações do usuário
            this.notificationsService.getNotificationsData()
                .then((notifications) => {
                    console.log('AppComponent: Notificações carregadas com sucesso:', notifications?.length || 0);
                })
                .catch((error) => {
                    console.error('AppComponent: Erro ao carregar notificações na inicialização:', error);
                });
                
        } catch (error) {
            console.error('AppComponent: Erro inesperado ao carregar notificações:', error);
        }
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

}
